/**
 * Manage Membership Page
 * ======================
 * Display membership tiers with "Coming Soon" for paid tiers.
 * FEATURES.paidMemberships controls whether checkout is enabled.
 * 
 * RESTRUCTURED: Only 3 tiers - Free, Gold, Diamond (Silver removed)
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Crown, CheckCircle, Star, Gem, Clock, Lock } from 'lucide-react';
import MembershipBadge from '../components/MembershipBadge';
import { FEATURES } from '../config/featureFlags';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MEMBERSHIP_TIERS = {
  free: {
    name: 'Free',
    price: 'Free',
    priceId: null,
    icon: Star,
    giftLimit: '$100',
    benefits: [
      'Basic profile',
      'Browse members',
      'Limited messaging',
      'Gift sending up to $100',
      'Basic discovery',
      'Access to select NearU events',
      'NearU Card access',
      'Basic proximity discovery'
    ],
    gradient: 'linear-gradient(135deg, #4A4A4A 0%, #2D2D2D 50%, #1A1A1A 100%)',
    textColor: '#FFFFFF',
    isFree: true
  },
  gold: {
    name: 'Gold',
    price: '$49.99/month',
    priceId: 'gold_membership',
    icon: Crown,
    giftLimit: '$5,000',
    benefits: [
      'Priority event access',
      'Enhanced profile visibility',
      'Advanced matching filters',
      'Gift sending up to $5,000',
      'VIP concierge access',
      'Unlimited messaging',
      'Advanced proximity features'
    ],
    gradient: 'linear-gradient(135deg, #FFF8DC 0%, #FFD700 20%, #DAA520 50%, #B8860B 80%, #996515 100%)',
    textColor: '#5D4E37',
    popular: true
  },
  diamond: {
    name: 'Diamond',
    price: '$99.99/month',
    priceId: 'diamond_membership',
    icon: Gem,
    giftLimit: '$10,000',
    benefits: [
      'Exclusive VIP events',
      'Maximum profile visibility',
      'Premium matching priority',
      'Gift sending up to $10,000',
      'Dedicated concierge',
      'VIP Bottle Service access',
      'Black Diamond verification badge',
      '24/7 priority support'
    ],
    gradient: 'linear-gradient(135deg, #F0F8FF 0%, #E6F3FF 15%, #B9F2FF 35%, #89CFF0 55%, #00BFFF 75%, #1E90FF 100%)',
    textColor: '#1A5276'
  }
};

const ManageMembershipPage = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const profileRes = await axios.get(`${API}/profile`);
      setProfile(profileRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#1A0F0A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ color: '#D7B56D', fontSize: '16px' }}>Loading...</div>
      </div>
    );
  }

  // Current tier - default to 'free' if no tier or paid memberships disabled
  // Map legacy 'silver' to 'gold' for backwards compatibility
  let rawTier = FEATURES.paidMemberships ? (profile?.membership_tier || 'free') : 'free';
  if (rawTier === 'silver') rawTier = 'gold'; // Legacy mapping
  const currentTier = rawTier;
  const currentTierConfig = MEMBERSHIP_TIERS[currentTier] || MEMBERSHIP_TIERS.free;
  const CurrentTierIcon = currentTierConfig.icon;

  // Only 3 tiers: Free, Gold, Diamond
  const tierOrder = ['free', 'gold', 'diamond'];

  return (
    <div style={{
      minHeight: '100vh',
      background: '#1A0F0A',
      paddingBottom: '100px'
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'rgba(26, 15, 10, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '16px',
        borderBottom: '1px solid rgba(165, 124, 51, 0.2)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px'
      }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none',
            border: 'none',
            color: '#E6D7C3',
            cursor: 'pointer',
            padding: '8px',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <ArrowLeft size={24} />
        </button>
        <h1 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '20px',
          color: '#E6D7C3',
          margin: 0
        }}>
          Manage Membership
        </h1>
      </div>

      <div style={{ padding: '20px 16px' }}>
        {/* Current Plan Card */}
        <div style={{
          background: 'rgba(26, 15, 10, 0.95)',
          borderRadius: '20px',
          border: '2px solid rgba(76, 175, 80, 0.5)',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            marginBottom: '16px'
          }}>
            <div style={{
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: currentTierConfig.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CurrentTierIcon size={28} color={currentTierConfig.textColor} />
            </div>
            <div>
              <div style={{ 
                fontFamily: 'Cinzel, serif',
                fontSize: '22px',
                color: '#E6D7C3',
                marginBottom: '4px'
              }}>
                {currentTierConfig.name} Membership
              </div>
              <div style={{ color: '#A57C33', fontSize: '14px' }}>
                {currentTierConfig.price}
              </div>
            </div>
            <div style={{ marginLeft: 'auto' }}>
              <MembershipBadge tier={currentTier} size="medium" />
            </div>
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 16px',
            background: 'rgba(76, 175, 80, 0.1)',
            borderRadius: '10px',
            border: '1px solid rgba(76, 175, 80, 0.3)'
          }}>
            <CheckCircle size={16} color="#4CAF50" />
            <span style={{ 
              color: '#4CAF50',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              Active
            </span>
          </div>

          <div style={{ 
            marginTop: '16px',
            padding: '14px',
            background: 'rgba(215, 181, 109, 0.08)',
            borderRadius: '12px'
          }}>
            <div style={{ color: '#A57C33', fontSize: '12px', marginBottom: '8px' }}>
              Gift Limit
            </div>
            <div style={{ color: '#E6D7C3', fontSize: '20px', fontWeight: '700', fontFamily: 'Cinzel, serif' }}>
              Up to {currentTierConfig.giftLimit}
            </div>
          </div>
        </div>

        {/* Tier Selection */}
        <h2 style={{
          fontFamily: 'Cinzel, serif',
          fontSize: '18px',
          color: '#E6D7C3',
          marginBottom: '16px'
        }}>
          Membership Tiers
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {tierOrder.map((tier) => {
            const config = MEMBERSHIP_TIERS[tier];
            const isCurrentTier = tier === currentTier;
            const isPaidTier = !config.isFree;
            const isComingSoon = isPaidTier && !FEATURES.paidMemberships;
            const Icon = config.icon;

            return (
              <div
                key={tier}
                data-testid={`tier-${tier}`}
                style={{
                  background: 'rgba(26, 15, 10, 0.95)',
                  borderRadius: '16px',
                  border: isCurrentTier 
                    ? '2px solid #4CAF50' 
                    : isComingSoon
                      ? '1px solid rgba(165, 124, 51, 0.15)'
                      : config.popular 
                        ? '2px solid rgba(218, 165, 32, 0.5)' 
                        : '1px solid rgba(165, 124, 51, 0.2)',
                  padding: '20px',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: isComingSoon ? 0.7 : 1
                }}
              >
                {/* Coming Soon Badge for paid tiers */}
                {isComingSoon && (
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '20px',
                    background: 'linear-gradient(135deg, #6B5B4D, #4A4039)',
                    color: '#E6D7C3',
                    padding: '4px 12px',
                    fontSize: '10px',
                    fontWeight: '700',
                    borderRadius: '0 0 8px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Clock size={10} />
                    COMING SOON
                  </div>
                )}

                {/* Popular Badge */}
                {config.popular && !isCurrentTier && !isComingSoon && (
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '20px',
                    background: 'linear-gradient(135deg, #FFD700, #DAA520)',
                    color: '#2C1B13',
                    padding: '4px 12px',
                    fontSize: '10px',
                    fontWeight: '700',
                    borderRadius: '0 0 8px 8px'
                  }}>
                    POPULAR
                  </div>
                )}

                {/* Current Badge */}
                {isCurrentTier && (
                  <div style={{
                    position: 'absolute',
                    top: '0',
                    right: '20px',
                    background: '#4CAF50',
                    color: '#FFF',
                    padding: '4px 12px',
                    fontSize: '10px',
                    fontWeight: '700',
                    borderRadius: '0 0 8px 8px'
                  }}>
                    CURRENT
                  </div>
                )}

                <div style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    background: isComingSoon 
                      ? 'linear-gradient(135deg, #4A4A4A, #2D2D2D)' 
                      : config.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    position: 'relative'
                  }}>
                    <Icon size={24} color={isComingSoon ? '#888' : config.textColor} />
                    {isComingSoon && (
                      <div style={{
                        position: 'absolute',
                        bottom: '-4px',
                        right: '-4px',
                        background: '#4A4039',
                        borderRadius: '50%',
                        padding: '2px'
                      }}>
                        <Lock size={12} color="#A57C33" />
                      </div>
                    )}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ 
                      fontSize: '18px', 
                      fontWeight: '700',
                      color: isComingSoon ? '#8A7355' : '#E6D7C3',
                      marginBottom: '4px'
                    }}>
                      {config.name}
                    </div>
                    <div style={{ 
                      color: isComingSoon ? '#6B5B4D' : '#D7B56D', 
                      fontSize: '16px', 
                      fontWeight: '600' 
                    }}>
                      {config.price}
                    </div>
                  </div>
                </div>

                {/* Gift Limit */}
                <div style={{
                  padding: '10px 14px',
                  background: isComingSoon 
                    ? 'rgba(74, 64, 57, 0.3)' 
                    : 'rgba(215, 181, 109, 0.08)',
                  borderRadius: '10px',
                  marginBottom: '16px'
                }}>
                  <div style={{ 
                    color: isComingSoon ? '#6B5B4D' : '#A57C33', 
                    fontSize: '11px', 
                    marginBottom: '4px' 
                  }}>
                    Gift Limit
                  </div>
                  <div style={{ 
                    color: isComingSoon ? '#8A7355' : '#E6D7C3', 
                    fontSize: '16px', 
                    fontWeight: '700' 
                  }}>
                    Up to {config.giftLimit}
                  </div>
                </div>

                {/* Benefits */}
                <ul style={{ 
                  listStyle: 'none', 
                  padding: 0, 
                  margin: '0 0 16px 0'
                }}>
                  {config.benefits.slice(0, isCurrentTier ? config.benefits.length : 4).map((benefit, idx) => (
                    <li 
                      key={idx}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '6px 0',
                        color: isComingSoon ? '#6B5B4D' : '#A57C33',
                        fontSize: '13px'
                      }}
                    >
                      <CheckCircle size={14} color={isComingSoon ? '#6B5B4D' : '#4CAF50'} />
                      {benefit}
                    </li>
                  ))}
                  {!isCurrentTier && config.benefits.length > 4 && (
                    <li style={{ 
                      color: isComingSoon ? '#4A4039' : '#8A7355', 
                      fontSize: '12px', 
                      paddingTop: '4px' 
                    }}>
                      +{config.benefits.length - 4} more benefits
                    </li>
                  )}
                </ul>

                {/* Action Button - Only show for non-current tiers */}
                {!isCurrentTier && (
                  isComingSoon ? (
                    // Coming Soon - Not clickable
                    <div
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'rgba(74, 64, 57, 0.3)',
                        border: '1px solid rgba(107, 91, 77, 0.3)',
                        borderRadius: '12px',
                        color: '#6B5B4D',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        cursor: 'not-allowed'
                      }}
                    >
                      <Lock size={16} />
                      Coming Soon
                    </div>
                  ) : config.isFree && isCurrentTier ? null : config.isFree ? (
                    // Free tier - already active message
                    <div
                      style={{
                        width: '100%',
                        padding: '14px',
                        background: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        borderRadius: '12px',
                        color: '#4CAF50',
                        fontSize: '15px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                      }}
                    >
                      <CheckCircle size={16} />
                      Your Current Plan
                    </div>
                  ) : null
                )}
              </div>
            );
          })}
        </div>

        {/* Premium Coming Soon Notice */}
        {!FEATURES.paidMemberships && (
          <div style={{
            marginTop: '24px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(215, 181, 109, 0.1) 0%, rgba(165, 124, 51, 0.05) 100%)',
            borderRadius: '16px',
            border: '1px solid rgba(215, 181, 109, 0.2)',
            textAlign: 'center'
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #D7B56D, #A57C33)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px'
            }}>
              <Crown size={24} color="#2C1B13" />
            </div>
            <h3 style={{
              fontFamily: 'Cinzel, serif',
              fontSize: '18px',
              color: '#E6D7C3',
              marginBottom: '8px'
            }}>
              Premium Memberships Coming Soon
            </h3>
            <p style={{
              color: '#A57C33',
              fontSize: '14px',
              lineHeight: '1.6',
              margin: 0
            }}>
              Gold and Diamond tiers with exclusive benefits are launching soon. 
              Stay tuned for premium features and higher gift limits.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageMembershipPage;
